import * as React from 'react';
import { useIntl } from 'react-intl';

import { useMutation } from '@apollo/client';

import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  VariantDisplayType,
  AddToCartTryFirstMutationVariables,
  AddToCartTryFirstMutation,
  CatalogVariantFragmentFragment,
  GetCatalogProductViewQuery,
  AvailabilityState,
} from '@notino/shared/definitions/types';
import { AdditionalServicesAvailability } from '@notino/web-tracking';

import ErrorBoundary from '@components/ErrorBoundary';
import { ICurrency, ILocale } from '@containers/App/model';
import { useAutoOpenWatchdogModal } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/useAutoOpenWatchdogModal';
import { AttributeTypes } from '@containers/ProductListing/model';
import { withProviders } from '@context/contextConfig';
import messages from '@messages';

import { IVariantsProps } from '../../model';
import { useDisplayType } from '../../utils';

import Accessories from './Accessories';
import { AddToCart } from './AddToCart';
import AvailableSets from './AvailableSets';
import { BellowTheLine } from './BellowTheLine';
import ConditionalFreeDelivery from './ConditionalFreeDelivery/loadable';
import {
  EngravingTryItFirstProvider,
  useEngravingTryItFirstContext,
} from './context/EngravingTryItFirst';
import { DeliveryTime, DeliveriesDisplayMode } from './DeliveryTime';
import DiscoveryBoxSelfChoice from './DiscoveryBoxSelfChoice';
import { Engraving } from './Engraving';
import { FreeGiftPackaging } from './FreeGiftPackaging';
import { Gift } from './Gift';
import { HairAnalyser } from './HairAnalyser';
import { useIsHairAnalyzerVisible } from './HairAnalyser/hooks/useIsHairAnalyzerVisible';
import { useTryItFirstCampaigns } from './hooks/useTryItFirstCampaigns';
import { Klarna } from './Klarna';
import { LoyaltyProgramBanner } from './LoyaltyProgramBanner';
import addToCartTryItFirstMutation from './queries/addToCartTryFirst.graphql';
import { SelectedVariant } from './SelectedVariant';
import Shadefinder from './Shadefinder';
import { SkinAnalyser } from './SkinAnalyser';
import { useIsSkinAnalyzerVisible } from './SkinAnalyser/hooks/useIsSkinAnalyzerVisible';
import { Container, HairAnalyzerWrapper, SkinAnalyzerWrapper } from './styled';
import { trackTryItFirstAddToCart } from './tracking';
import { TryItFirst } from './TryItFirst';
import { useShouldRenderTagDiscount } from './utils';
import { isInfantFormula } from './utils/isInfantFormula';
import { useShouldRenderEligibleAction } from './utils/useShouldRenderEligibleAction';
import { VariantGrouping } from './VariantGrouping';
import { Variants } from './Variants';
import { ActionCard } from './VariantsInSelectBox/ActionCard';
import { VoucherDiscountMaster } from './VoucherDiscount';
import { useResolvedVoucherDiscount } from './VoucherDiscount/useResolvedVoucherDiscount';
import { WatchdogAndSimilarProducts } from './WatchdogAndSimilarProducts';

export interface IProductBaseInfoProps {
  selectedVariant: CatalogVariantFragmentFragment;
  product: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >;
  variants: CatalogVariantFragmentFragment[];
  currency: ICurrency;
  locale: ILocale;
  brand: string;
  imageDomainPath: string;
  variantName: React.ReactNode;
  deliveriesDisplayMode?: DeliveriesDisplayMode;
  engravingAvailable: AdditionalServicesAvailability;
  tryItFirstAvailable: AdditionalServicesAvailability;
  shadefinderAvailable: boolean;
}

const ProductBaseInfoComponent: React.FC<IProductBaseInfoProps> = ({
  selectedVariant,
  currency,
  product,
  variants,
  locale,
  brand,
  variantName,
  deliveriesDisplayMode,
  engravingAvailable,
  tryItFirstAvailable,
  shadefinderAvailable,
}) => {
  const { formatMessage } = useIntl();
  const [productQuantity, setProductQuantity] = React.useState(1);

  const [tryItFirstAddToCart] = useMutation<
    AddToCartTryFirstMutation,
    AddToCartTryFirstMutationVariables
  >(addToCartTryItFirstMutation, {
    refetchQueries: ['getServicesIncluded'],
    onCompleted: () => {
      trackTryItFirstAddToCart(selectedVariant.productCode);
    },
  });

  const {
    state: { withTryItFirst },
    actions: { clear },
  } = useEngravingTryItFirstContext();

  useAutoOpenWatchdogModal(selectedVariant);

  React.useEffect(() => {
    clear();
  }, [selectedVariant.webId, clear]);

  const tryItFirstCampaigns = useTryItFirstCampaigns(product.campaigns);

  const handleAddTryItFirst = React.useCallback(async () => {
    const tryItFirstCampaignForSelectedProduct = tryItFirstCampaigns.find(
      (campaign) => campaign?.productId === selectedVariant.webId
    );

    if (withTryItFirst && tryItFirstCampaignForSelectedProduct) {
      await tryItFirstAddToCart({
        variables: {
          productCode: selectedVariant.productCode,
          productId: selectedVariant.webId,
          tryItFirstId: tryItFirstCampaignForSelectedProduct.campaignId ?? '',
        },
      });
    }

    clear();
  }, [
    withTryItFirst,
    tryItFirstCampaigns,
    selectedVariant,
    tryItFirstAddToCart,
    clear,
  ]);

  const displayType = useDisplayType(product);

  const renderTagDiscount = useShouldRenderTagDiscount(selectedVariant);
  const shouldRenderActionCard = useShouldRenderEligibleAction();

  const inStock = selectedVariant.stockAvailability.code !== IStock.outOfStock;
  const { deliveries } = product;

  const commonVariantsProps: IVariantsProps = {
    selectedId: selectedVariant.webId,
    selectedVariant,
    variants,
    locale,
    currency,
  };

  const shouldRenderTryItFirst = React.useMemo(
    () =>
      tryItFirstCampaigns.find(
        (campaign) => campaign?.productId === selectedVariant.webId
      ),
    [tryItFirstCampaigns, selectedVariant]
  );

  const voucherDiscount = useResolvedVoucherDiscount();

  const conditionalFreeDelivery =
    selectedVariant.attributes[AttributeTypes.ConditionalFreeDelivery];

  const shouldShowSkinAnalyzer = useIsSkinAnalyzerVisible();
  const shouldShowHairAnalyzer = useIsHairAnalyzerVisible();

  const selectedVariantComponent = (
    <SelectedVariant
      displayType={displayType}
      renderTagDiscount={renderTagDiscount && !isInfantFormula(selectedVariant)}
      variantName={variantName}
      hideLine={
        displayType === VariantDisplayType.ShadesDrawer ||
        !!voucherDiscount ||
        !!shouldRenderActionCard
      }
      hideVariantName={displayType === VariantDisplayType.ShadesDrawer}
    />
  );

  return (
    <Container>
      <ErrorBoundary>
        {selectedVariantComponent}

        {shouldRenderActionCard && <ActionCard />}

        {voucherDiscount && (
          <VoucherDiscountMaster
            currency={currency}
            voucherDiscount={voucherDiscount}
            hideLine={conditionalFreeDelivery}
          />
        )}

        {conditionalFreeDelivery && (
          <ConditionalFreeDelivery
            conditionalFreeDelivery={conditionalFreeDelivery}
            leftPadding={!!voucherDiscount}
          />
        )}

        <ErrorBoundary>
          {shadefinderAvailable && (
            <Shadefinder masterId={product.webMasterId} />
          )}
        </ErrorBoundary>

        <VariantGrouping />
        <Variants displayType={displayType} {...commonVariantsProps} />

        <ErrorBoundary>
          {((product.features?.availableSets?.sets?.length ?? 0) > 0 ||
            (selectedVariant.features?.availableSets?.sets?.length ?? 0) >
              0) && <AvailableSets />}
        </ErrorBoundary>

        <ErrorBoundary>
          {(product.features?.accessories?.products?.length ?? 0) > 0 && (
            <Accessories />
          )}
        </ErrorBoundary>

        <ErrorBoundary>
          <DiscoveryBoxSelfChoice />
        </ErrorBoundary>

        <ErrorBoundary>
          <BellowTheLine variant={selectedVariant} locale={locale} />
        </ErrorBoundary>

        <AddToCart
          key={selectedVariant.catalogId}
          product={product}
          currency={currency}
          variant={selectedVariant}
          quantity={productQuantity}
          setQuantity={setProductQuantity}
          orderUnit={formatMessage(messages.orderUnit)}
          productCode={product.masterProductCode}
          onProductAdded={handleAddTryItFirst}
          engravingAvailable={engravingAvailable}
          tryItFirstAvailable={tryItFirstAvailable}
        />

        {selectedVariant.availability.state === AvailabilityState.CanBeBought &&
          selectedVariant.attributes[AttributeTypes.Klarna] && <Klarna />}

        <WatchdogAndSimilarProducts
          currentVariant={selectedVariant}
          product={product}
        />

        <LoyaltyProgramBanner productQuantity={productQuantity} />

        {selectedVariant.features?.engraving?.allowed && (
          <Engraving
            code={selectedVariant.orderCode ?? ''}
            currency={currency}
            maxChars={selectedVariant.features.engraving.characterCount ?? 0}
            price={selectedVariant.features.engraving.config?.price ?? 0}
            image={selectedVariant.features.engraving.image ?? undefined}
            imageAlt={selectedVariant.media?.mainImage?.alt ?? ''}
            engravingAvailable={engravingAvailable}
          />
        )}

        {shouldRenderTryItFirst && (
          <TryItFirst availability={tryItFirstAvailable} />
        )}

        <ErrorBoundary>
          {selectedVariant.campaignConfig && (
            <FreeGiftPackaging
              brand={brand}
              currency={currency}
              campaign={selectedVariant.campaignConfig}
            />
          )}
        </ErrorBoundary>

        <ErrorBoundary>
          <Gift />
        </ErrorBoundary>

        {inStock && (
          <DeliveryTime
            deliveries={deliveries}
            displayMode={deliveriesDisplayMode}
            selectedCatalogId={selectedVariant.catalogId}
          />
        )}

        {shouldShowSkinAnalyzer && (
          <SkinAnalyzerWrapper>
            <SkinAnalyser key={product.catalogMasterId} />
          </SkinAnalyzerWrapper>
        )}

        {shouldShowHairAnalyzer && (
          <HairAnalyzerWrapper>
            <HairAnalyser key={product.catalogMasterId} />
          </HairAnalyzerWrapper>
        )}
      </ErrorBoundary>
    </Container>
  );
};

export const ProductBaseInfo = withProviders<
  IProductBaseInfoProps & Record<string, unknown>
>(EngravingTryItFirstProvider)(ProductBaseInfoComponent);
